/**
 * useIntegrationSchema Hook (Story 5.34, Task 6)
 *
 * Fetches the available metrics and dimensions for a given integration platform.
 * Uses React Query with 5-minute stale time since schemas rarely change.
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/apiClient";
import type { MetricDefinition, DimensionDefinition } from "@provenleap/integrations";

interface IntegrationSchema {
  platform: string;
  metrics: MetricDefinition[];
  dimensions: DimensionDefinition[];
  apiVersion: string;
}

export function useIntegrationSchema(integrationId: string | undefined) {
  const { data, isLoading } = useQuery<IntegrationSchema>({
    queryKey: ["integration-schema", integrationId],
    queryFn: async () => {
      const res = await apiGet(`/api/integrations/${integrationId}/schema`);
      if (!res.ok) {
        throw new Error(`Failed to fetch schema for ${integrationId}`);
      }
      const json = await res.json();
      return json.data;
    },
    enabled: !!integrationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    metrics: data?.metrics ?? [],
    dimensions: data?.dimensions ?? [],
    isLoading,
  };
}
